import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';

/*
 * Job Comments Content
 *
 * Shared, route-independent view of the job comments filters + table.
 * Rendered both as a full page (ManageJobComment, variant="page") and inside
 * the slide-out Main Comment Modal (JobCommentsModal, variant="modal").
 */
import { Button, Col, Row as BootstrapRow } from 'react-bootstrap';
import { WithTranslation, withTranslation } from 'react-i18next';
import { IinstallBasePopulated, Ijob, IjobComment, ItableFiltersReducer, Itile, Iuser } from '../../models';
import { IinitialState } from '../../reducers';

import { Redirect, RouteComponentProps } from 'react-router-dom';
import { useTable, useSortBy, Column, useFlexLayout, usePagination } from 'react-table';
import {
    setSelectedJobComment,
    setTableFilter,
    submitSignature,
    toggleEditJobCommentModal,
    toggleJobCommentsModal,
    toggleModalVerificationSignaturePad
} from '../../actions/manageJobCommentsActions';
import { initialJob, initialJobComment } from '../../reducers/initialState';

import moment from 'moment';
import { FieldConfig } from 'react-reactive-form';
import { connect, useSelector } from 'react-redux';
import { selectIsJobReadOnly } from '../../reducers/commonSelectors';
import { constants } from '../../constants/constants';
import { jobStatusEnum, jobTypesIdEnum } from '../../models-enums';
import { selectIsLoading } from '../../reducers/commonReducers';
import { filterInstallBases } from '../../reducers/manageInventoryReducer';
import { selectVisibleJobComments } from '../../reducers/manageJobCommentsReducer';
import { Iphoto } from '../../models';
import { Banner } from '../common/Banner';
import CommonModal from '../common/CommonModal';
import { FormUtil } from '../common/FormUtil';
import SearchTableForm from '../common/SearchTableForm';

import { EditJobCommentContainer } from './EditJobCommentContainer';
import VerificationSignatureModal from './VerificationSignatureModal';

interface Iprops extends RouteComponentProps<any> {
    // 'page' renders the Banner and redirects home when no active job;
    // 'modal' omits the Banner and renders the edit form as a stacked second modal.
    variant: 'page' | 'modal';
    loading?: boolean; // injected by connect (mapStateToProps)
}

interface IdispatchProps {
    toggleEditJobCommentModal: typeof toggleEditJobCommentModal;
    toggleJobCommentsModal: typeof toggleJobCommentsModal;
    toggleModalVerificationSignaturePad: typeof toggleModalVerificationSignaturePad;
    showEditJobCommentModal: boolean;
    showVerificationSignatureModal: boolean;
    filteredInstallBases: IinstallBasePopulated[];
    submitSignature: typeof submitSignature;
    setTableFilter: typeof setTableFilter;
    tableFilters: ItableFiltersReducer;
    tableData: IjobComment[];
    setSelectedJobComment: typeof setSelectedJobComment;
    data: { [key: string]: IjobComment };
    initComplete: boolean;
    fseUsers: Iuser[];
    selectedJobID: string;
    selectedJob: Ijob;
    photos: { [key: string]: Iphoto };
}

// Job notes open on the smallest page size the rows dropdown offers rather than the
// app-wide default of 100, so the newest notes are readable without scrolling a wall
// of rows. Must stay one of constants.tablePageSizeOptions or the dropdown shows blank.
const jobCommentsPageSize = 25;

const JobCommentsContent: React.FC<Iprops & IdispatchProps & WithTranslation> = props => {
    const isJobReadOnly = useSelector(selectIsJobReadOnly);
    const { t, variant, tableData, tableFilters, fseUsers, selectedJob, loading, initComplete, selectedJobID, photos } =
        props;
    const isModal = variant === 'modal';

    const [selectedRow, setSelectedRow] = useState<any>(null);
    // Tile is derived from the job comments route so colors stay consistent in
    // both the page and modal variants (no dependency on the live pathname).
    const currentTile: Itile = useMemo(() => constants.getTileByURL('/job_comments'), []);
    const [filterLength, setFilterLength] = useState<number>(0);
    const [buttonInAction] = useState<boolean>(false);

    // Build columns using useMemo for performance.
    // mobileLabel is rendered as each cell's `data-label` so the stacked
    // (vertical) mobile layout can show the column name next to each value.
    const columns = useMemo((): (Column<IjobComment> & { mobileLabel: string })[] => {
        return [
            {
                id: 'date',
                Header: <span>{t('date')}</span>,
                mobileLabel: t('date'),
                accessor: (row: IjobComment) => {
                    return row.createDate ? moment.utc(row.createDate).local(true).format('DD-MMM-YY') : 'n/a';
                },
                width: 100
            },
            {
                Header: t('engineer'),
                id: 'engineer',
                mobileLabel: t('engineer'),
                accessor: (row: IjobComment) => {
                    const foundUser = fseUsers.find(user => user.id === row.userID);
                    return foundUser ? `${foundUser.first} ${foundUser.last}` : '';
                }
            },
            {
                id: 'comment',
                Header: t('comment'),
                mobileLabel: t('comment'),
                accessor: (row: IjobComment) => {
                    return <div dangerouslySetInnerHTML={{ __html: row.text }} />;
                },
                minWidth: 150
            },
            {
                Header: t('type'),
                id: 'type',
                mobileLabel: t('type'),
                accessor: (row: IjobComment) => {
                    return row.isDeficiency ? t('deficiency') : t('info');
                },
                width: 120
            },
            {
                Header: t('images'),
                id: 'images',
                mobileLabel: t('images'),
                accessor: (row: IjobComment) => {
                    const count = Object.values(photos).filter(
                        (p: Iphoto) => !p.isDeleted && p.jobCommentID === row.id
                    ).length;
                    return count > 0 ? count : '';
                },
                width: 70
            }
        ];
    }, [t, fseUsers, photos]);

    // Build search field config
    const buildSearchFieldConfig = (): FieldConfig => {
        const { userID, commentType } = tableFilters;
        const disabled = false;
        const mainSearchControls = {
            userID: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'manageJobComments:labelUser',
                    options: FormUtil.convertToOptions(fseUsers),
                    colWidth: 6,
                    colWidthLarge: 4,
                    type: 'select',
                    placeholder: 'placeholderUser',
                    isClearable: true
                },
                formState: {
                    value: userID,
                    disabled
                }
            },
            commentType: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: 'manageJobComments:labelType',
                    options: constants.jobCommentTypeOptions,
                    colWidth: 6,
                    colWidthLarge: 4,
                    type: 'select',
                    placeholder: 'placeholderType',
                    isClearable: true
                },
                formState: { value: commentType, disabled }
            }
        };

        return { controls: { ...mainSearchControls } };
    };

    const [searchFieldConfig, setSearchFieldConfig] = useState<FieldConfig>(buildSearchFieldConfig());

    // Use react-table v7 hooks with sorting, pagination and flex layout
    const tableInstance = useTable(
        {
            columns: columns as Column<IjobComment>[],
            data: tableData,
            initialState: {
                pageIndex: tableFilters.page || 0,
                pageSize: tableFilters.rows || jobCommentsPageSize
            } as any,
            manualPagination: false,
            pageCount: Math.ceil(tableData.length / (tableFilters.rows || jobCommentsPageSize))
        } as any,
        useSortBy,
        usePagination,
        useFlexLayout
    ) as any;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        rows,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    // Sync page and page size with redux tableFilters
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // the modal has no paginator (MMG-4435), so only the page variant owns these filters
        if (isModal) {
            return;
        }
        if (pageIndex !== tableFilters.page) {
            props.setTableFilter({ page: pageIndex });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageIndex, tableFilters.page]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (isModal) {
            return;
        }
        if (pageSize !== tableFilters.rows) {
            props.setTableFilter({ rows: pageSize, page: 0 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize, tableFilters.rows]);

    // Clear selected row when the edit modal closes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (props.showEditJobCommentModal === false) {
            setSelectedRow(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.showEditJobCommentModal]);

    // Update search field config when table filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const newFieldConfig = buildSearchFieldConfig();
        setSearchFieldConfig(newFieldConfig);
        setFilterLength(Object.keys(newFieldConfig.controls).length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableFilters, fseUsers]);

    // Handle row click
    const getTrProps = (rowInfo: any) => {
        if (rowInfo) {
            return {
                onClick: () => {
                    if (!buttonInAction) {
                        setSelectedRow(rowInfo.index);
                        props.toggleEditJobCommentModal();
                        props.setSelectedJobComment(rowInfo.original);
                    }
                },
                style: {
                    background:
                        rowInfo.index === selectedRow
                            ? constants.colors[`${currentTile.color}Tr` as keyof typeof constants.colors]
                            : ''
                }
            };
        } else {
            return {};
        }
    };

    // Handle search value changes
    const onSearchValueChanges = (value: any, key: string) => {
        props.setTableFilter({ [key]: value, page: 0 });
    };

    if (initComplete === false) {
        return (
            <Col xs={12}>
                <h4> loading... </h4>
            </Col>
        );
    }

    // return home if there is no job or the job has been completed, unless the approver is viewing it (page variant only)
    if (!selectedJobID.length || (selectedJob.status === jobStatusEnum.completed && !isJobReadOnly)) {
        return isModal ? null : (
            <Redirect
                to={{
                    pathname: '/'
                }}
            />
        );
    }

    return (
        <div
            className={`manage-job-comments${isModal ? ' job-comments-modal-content' : ''}`}
            style={{
                ['--filter-size' as string]: filterLength
            }}
            data-manage="true"
        >
            {!isModal && <Banner title={t('bannerTitle')} img={currentTile.srcBanner} color={currentTile.color} />}
            <BootstrapRow>
                {/* In the modal variant the action buttons live in the modal title bar
                    (rendered by JobCommentsModal), so the filters take the full width. */}
                <Col xs={isModal ? 12 : 7}>
                    <SearchTableForm
                        fieldConfig={searchFieldConfig}
                        handleSubmit={() => {
                            return;
                        }}
                        loading={!!loading}
                        colorButton={constants.colors[`${currentTile.color}Button` as keyof typeof constants.colors]}
                        t={t}
                        subscribeValueChanges={true}
                        onValueChanges={onSearchValueChanges}
                        showSearchButton={false}
                    />
                </Col>
                {!isModal && (
                    <Col
                        className="search-actions-container job-comments"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            height: '78px',
                            marginTop: '2px',
                            padding: '1rem',
                            width: 'auto',
                            gap: '16px'
                        }}
                    >
                        {selectedJob.jobTypeID === jobTypesIdEnum.verification && (
                            <Button
                                variant="link"
                                style={{ whiteSpace: 'nowrap' }}
                                onClick={() => {
                                    props.toggleModalVerificationSignaturePad();
                                }}
                            >
                                {t('manageJobComments:hospitalRepSignature')}
                            </Button>
                        )}
                        <Button
                            variant="link"
                            style={{ whiteSpace: 'nowrap' }}
                            onClick={() => {
                                props.toggleEditJobCommentModal();
                                props.setSelectedJobComment(initialJobComment);
                            }}
                        >
                            {t('manageJobComments:newJobCommentButton')}
                        </Button>
                    </Col>
                )}
            </BootstrapRow>

            {/* Table */}
            <div
                {...getTableProps()}
                className={`ReactTable beacon-table job-comments-table -highlight ${currentTile.color} flex-layout`}
            >
                <div className="rt-thead -header">
                    {headerGroups.map((headerGroup: any) => (
                        <div
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.getHeaderGroupProps().key}
                            className="rt-tr"
                        >
                            {headerGroup.headers.map((column: any) => {
                                const isSorted = column.isSorted;
                                const isSortedDesc = column.isSortedDesc;
                                return (
                                    <div
                                        {...column.getHeaderProps()}
                                        key={column.getHeaderProps().key}
                                        className="rt-th"
                                    >
                                        <div>
                                            {column.render('Header')}
                                            {isSorted ? (isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                <div {...getTableBodyProps()} className="rt-tbody">
                    {(isModal ? rows : page).map((row: any, i: number) => {
                        prepareRow(row);
                        const rowProps = getTrProps({ index: i, original: row.original });
                        return (
                            <div
                                {...row.getRowProps()}
                                key={row.getRowProps().key}
                                onClick={rowProps.onClick}
                                style={rowProps.style}
                                className={`rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`}
                            >
                                {row.cells.map((cell: any) => {
                                    return (
                                        <div
                                            {...cell.getCellProps()}
                                            key={cell.getCellProps().key}
                                            className="rt-td"
                                            data-label={cell.column.mobileLabel}
                                        >
                                            {cell.render('Cell')}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/*
              * MMG-4435 round 2: the modal has no pagination row at all - Vanessa's point on both
              * tickets is that the whole section costs real estate the mobile view cannot spare. The
              * modal renders every note and scrolls; the /job_comments page variant keeps the full
              * paginator, where there is room for it and the rows limit is worth having.
              */}
            {!isModal && (
                <div className="table-pagination">
                    <button onClick={() => previousPage()} disabled={!canPreviousPage} className="btn">
                        Previous
                    </button>

                <div className="controls">
                    <label htmlFor="page-input">Page </label>
                    <span>
                        <input
                            id="page-input"
                            type="number"
                            min={1}
                            max={pageCount}
                            className="page-input"
                            defaultValue={pageIndex + 1}
                            key={`page-input-${pageIndex}`}
                            onChange={e => {
                                const gotoPageNum = e.target.value ? Number(e.target.value) - 1 : 0;
                                if (gotoPageNum >= 0 && gotoPageNum < pageCount) {
                                    gotoPage(gotoPageNum);
                                }
                            }}
                            onWheel={e => e.currentTarget.blur()}
                        />
                    </span>
                    <span>of </span>
                    <span>{pageCount}</span>

                    <select
                        value={pageSize}
                        className="table-page-size-select"
                        onChange={e => {
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {constants.tablePageSizeOptions.map(size => (
                            <option key={size} value={size}>
                                {size} {t('common:rows')}
                            </option>
                        ))}
                    </select>
                </div>

                    <button onClick={() => nextPage()} disabled={!canNextPage} className="btn">
                        Next
                    </button>
                </div>
            )}

            {isModal && (
                <div className="form-buttons text-right">
                    <Button
                        variant="default"
                        type="button"
                        className="cancel-button"
                        onClick={props.toggleJobCommentsModal}
                    >
                        {t('common:close')}
                    </Button>
                </div>
            )}

            <VerificationSignatureModal
                colorButton={constants.colors[`${currentTile.color}Button` as keyof typeof constants.colors]}
                installBasesPopulated={props.filteredInstallBases}
                selectedJob={selectedJob}
                secondModal={isModal}
                secondWideModal={isModal}
            />
            <CommonModal
                className="job-edit"
                show={props.showEditJobCommentModal}
                onHide={props.toggleEditJobCommentModal}
                title={t('titleJobCommentModal')}
                container={document.getElementById('two-pane-layout')}
                secondModal={isModal}
                secondWideModal={isModal}
            >
                <EditJobCommentContainer
                    colorButton={constants.colors[`${currentTile.color}Button` as keyof typeof constants.colors]}
                    t={t}
                    location={props.location}
                    match={props.match}
                    history={props.history}
                />
            </CommonModal>
        </div>
    );
};

const mapStateToProps = (state: IinitialState) => {
    const selectedJob = state.manageJob.jobsByID[state.manageJob.selectedJob.id] || initialJob;
    const filteredJobComments = selectVisibleJobComments(
        state.manageJobComment.jobCommentsByID,
        state.manageJobComment.tableFilters,
        selectedJob.id
    );
    const filteredInstallBases = filterInstallBases(state);
    return {
        user: state.user,
        loading: selectIsLoading(state),
        showEditJobCommentModal: state.manageJobComment.showEditJobCommentModal,
        showVerificationSignatureModal: state.manageJobComment.showVerificationSignatureModal,
        tableData: filteredJobComments,
        tableFilters: state.manageJobComment.tableFilters,
        filteredInstallBases,
        data: state.manageJobComment.jobCommentsByID,
        initComplete: state.syncStatus.isInitialSyncComplete,
        fseUsers: state.manageJob.fseUsers,
        selectedJobID: state.manageJob.selectedJob.id,
        selectedJob,
        photos: state.photos.photos
    };
};

export default withTranslation('manageJobComments')(
    connect(mapStateToProps, {
        toggleEditJobCommentModal,
        toggleJobCommentsModal,
        setTableFilter,
        setSelectedJobComment,
        submitSignature,
        toggleModalVerificationSignaturePad
    })(JobCommentsContent)
);
